import config from "@Config/index";
import {
  ROOM_TYPE_FILTER,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
} from "@Pages/Search/components/FilterSection/data/filtersList";
import { updateDynamicFilterCountData } from "@Pages/Search/data/searchPageReducer";
import { convertToFilterObj } from "@Pages/Search/utils/formatFilterState";
import { withStrictMonthCounts } from "@Pages/Search/utils/moveInMonthWindow";
import axios from "axios";
import { COUNTRIES_WITH_MONTHLY_DURATION } from "constants/configConstants";
import getCookie from "helpers/getCookie";
import {
  applySearchGeoRadius,
  resolveRadiusExpandEnabled,
} from "@Pages/Search/utils/geoRadiusExpand";

const fetchMoveInMonths = async ({
  currFilters,
  filters,
  region,
  searchListDispatch,
  dynamicFilterCountData,
  isWhitelabel,
  partner_id,
  isUniversityPartner,
  isRestrictedSearchEnabled,
  isRadiusExpanded = false,
  radiusExpandEnabled = false,
}: any) => {
  const filterParam = {
    ...convertToFilterObj(currFilters),
    room_types: ROOM_TYPE_FILTER.list
      .map((it) => it.value)
      .filter((it) => currFilters.roomType?.includes(it))
      .join(","),
    bathroom_types: SHARING_OPTIONS_BATHROOM.list
      .map((it) => it.value)
      .filter((it) => currFilters.roomType?.includes(it))
      .join(","),
    kitchen_types: SHARING_OPTIONS_KITCHEN.list
      .map((it) => it.value)
      .filter((it) => currFilters.roomType?.includes(it))
      .join(","),
  };
  if (filterParam["meta_types"]) {
    delete filterParam["meta_types"];
  }
  if (!filterParam["available"]) {
    delete filterParam["available"];
  }

  const finalFilter = Object.keys(filterParam).reduce((acc: any, it) => {
    if (filterParam[it]) {
      acc[it] = filterParam[it];
    }
    return acc;
  }, {});

  const params = {
    status: "active",
    // price_histogram is included so this response does not wipe the budget filter's bars out
    // of the store — it replaces the payload FilterSection's fuller request wrote.
    // `price_stats` rides along with `price_histogram`: this response replaces
    // `dynamicFilterCountData.data` wholesale, and without it the budget panel loses its floor
    // (£10 -> £0 in London) as soon as the move-in-month sheet is opened.
    agg_keys: [
      "available",
      "move_in_month",
      "flexible_move_in",
      "price_histogram",
      "price_stats",
    ].join(","),
    price_histogram_duration: COUNTRIES_WITH_MONTHLY_DURATION.includes(
      region?.location?.country?.short_name,
    )
      ? "monthly"
      : "weekly",
    ...(currFilters.locality &&
      currFilters.university && {
        locality_canonical_name: filters.locality,
        region_canonical_name: currFilters.university,
        locality_radius: 10000,
      }),
    ...(currFilters.locality
      ? {
          locality_canonical_name: currFilters.locality || region?.canonical_name,
        }
      : {
          region_canonical_name: currFilters.university || region?.canonical_name,
        }),
    ...finalFilter,
    ...(finalFilter?.range_available_price && {
      ...(COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)
        ? { range_available_price: finalFilter?.range_available_price }
        : { weekly_range_price: finalFilter?.range_available_price }),
      duration: COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)
        ? "monthly"
        : "weekly",
    }),
    region_type: "circle",
    region_radius:
      finalFilter.region_radius || (region?.location?.country?.short_name === "AE" ? 60000 : 20000),
  };

  // Use 30000 region_radius for nearby requests
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const localityParam = urlParams.get("locality");
    if (localityParam && localityParam.includes("near-by")) {
      const nearbyLat = getCookie("nearby_lat");
      const nearbyLng = getCookie("nearby_lng");
      if (nearbyLat && nearbyLng) {
        params.region_radius = 30000;
      }
    }
  }

  if ((isWhitelabel && isUniversityPartner) || isRestrictedSearchEnabled) {
    params.whitelabel_partner_id = partner_id;
  }

  // The budget stays on this request (weekly_range_price for weekly countries), as on release.
  // The price_histogram aggregation ignores the price filter, so the bars are unaffected.
  delete params["range_available_price"];

  applySearchGeoRadius(
    params,
    currFilters,
    region,
    isRadiusExpanded,
    resolveRadiusExpandEnabled(radiusExpandEnabled),
  );

  if (
    (params.region_canonical_name &&
      JSON.stringify(params) !== JSON.stringify(dynamicFilterCountData.params)) ||
    (params.locality_canonical_name &&
      JSON.stringify(params) !== JSON.stringify(dynamicFilterCountData.params))
  ) {
    const response = await axios(
      // Note: This api needs to be updated to fix the count for whitelabel
      `${config.API_URL}/api/v1/inventories/inventory_filter_count`,
      {
        // Exact move-in month counts, matching the listing (API params only).
        params: withStrictMonthCounts({
          ...params,
          use_java_api: true,
        }),
      },
    );

    const data = response?.data?.data;
    if (data?.data) {
      data.data.meta_types = {
        ...data.data.room_types,
        ...data.data.kitchen_types,
        ...data.data.bathroom_types,
      };
    }
    searchListDispatch(
      updateDynamicFilterCountData({
        isLoading: false,
        data,
        params,
      }),
    );
  }
};
export default fetchMoveInMonths;
